import DojoView from "csi/page/DojoView";
import xml from "csi/xml";
import { Box, Button, FlexColumn, Grid, Message, Tab, TabPanel, Tabs } from "designSystem/index";
import { useLocale } from "designSystem/locale";
import { observer } from "mobx-react-lite";
import React, { FunctionComponent } from "react";
import { getFileVaultRequestUrl } from "src/c8-platform/core/request";
import { useSustainabilityProfileStore } from "./SustainabilityProfileStore";

const REVISION_STATE_APPROVED = "Revision.State:APPROVED";

interface SustainabilityProfileProps {}

const SustainabilityProfileTabPanel: FunctionComponent<{ index: number; tabPanel: string }> = ({ index, tabPanel }) => {
	const {
		locationRevisionUrl,
		selectedTabIndex,
		onViewLoad,
		viewNodeType,
	} = useSustainabilityProfileStore();

	return (
		<TabPanel
			index={index}
			key={`Tab${viewNodeType}${index}`}
			py={2}
			selectedTabIndex={selectedTabIndex}
		>
			<DojoView
				forceReadOnly
				nodeUrl={locationRevisionUrl}
				onViewLoad={onViewLoad}
				viewId={tabPanel}
				viewNodeType={viewNodeType}
			/>
		</TabPanel>
	);
};

const SustainabilityProfileTDSView: FunctionComponent = observer(() => {
	const {
		locationRevisionUrl,
		pdfRequestsExist,
		pdfURL,
		requestFailed,
		retryPDFGeneration,
		revisionState,
		viewNodeType,
	} = useSustainabilityProfileStore();
	const loc = useLocale();

	// If PDF exists on the revision, show PDF. Else show TDS view (TitleBlock+Properties)
	// Show banner if there are PDF Requests
	return (
		<div
			style={{
				height: "100%",
			}}
		>
			{xml.isEmptyURL(pdfURL) ? (
				<div
					style={{
						height: "100%",
					}}
				>
					{revisionState === REVISION_STATE_APPROVED && pdfRequestsExist &&
						(!requestFailed ? (
							<Message
								titleText={loc.resource("SPBannerPDFGenerationPending")}
								variant="info"
							/>
						)
							: (
								<Message
									titleText={loc.resource("SPBannerPDFGenerationFailed")}
									variant="error"
								>{{
										buttons: (
											<Grid container>
												<Grid item>
													<Button design="secondary" onClick={retryPDFGeneration}>{loc.resource("SPRetryPDFGenerationButton")}</Button>
												</Grid>
											</Grid>
										),
									}}
								</Message>
							)
						)
					}
					<Box p={2}>
						<DojoView
							nodeUrl={locationRevisionUrl}
							viewId="TDS"
							viewNodeType={viewNodeType}
						/>
					</Box>
				</div>
			)
				: (
					<iframe
						allowFullScreen
						src={getFileVaultRequestUrl(pdfURL)}
						style={{ 
							flex: 1,
							width: "100%",
							height: "100%",
						}}
					/>
				)}
		</div>
	);
});

const SustainabilityProfileView: FunctionComponent<SustainabilityProfileProps> = observer(function SustainabilityProfile() {
	const {
		handleTabChange,
		isTemplate,
		selectedTabIndex,
		tabPanels,
		viewNodeType,
	} = useSustainabilityProfileStore();
	const loc = useLocale();

	return (
		<FlexColumn>
			{isTemplate
				? (
					<>
						<Tabs
							component="div"
							onChange={handleTabChange}
							value={selectedTabIndex}
							variant="scrollable"
						>
							{tabPanels.map((tabPanel) => (
								<Tab key={`${viewNodeType}${tabPanel}`} label={loc.resource(tabPanel)} />
							))}
						</Tabs>
						{tabPanels.map((tabPanel, index) => (
							<SustainabilityProfileTabPanel 
								index={index}
								key={index}
								tabPanel={tabPanel} 
							/>
						))}
					</>
				)
				: (
					<SustainabilityProfileTDSView />
				)}
		</FlexColumn>
	);
});

export default SustainabilityProfileView;
